import React, { useState } from "react";
import { Button as DialogButton } from "components/Graphs/Button";
import { Checkbox } from "components/Graphs/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import type {
  LaunchableManagedSecretReference,
  LaunchableParameter,
} from "server";
import type { ManagedSecretSummary } from "server/devplane/managedSecrets/api";
import { LaunchableParameterBindingForm } from "components/Launchables/DeployRedesign/shared/configuration/parameters/LaunchableParameters";
import type { DeploymentConfirmationStep } from "components/Launchables/DeployRedesign/shared/hooks/useDeployRequest";

interface NgcApiKeyDeployWithoutKeyConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  steps: DeploymentConfirmationStep[];
  stepIndex: number;
  computeHasErrors: boolean;
  computeIsOutOfStock?: boolean;
  selectedCompute?: {
    details: string[];
    providerName?: string | null;
    highlightFirstDetail?: boolean;
  } | null;
  onSelectCompute: () => void;
  onNextStep: () => void;
  onConfirmDeploy: () => Promise<void> | void;
  onIncludeNgcApiKeyAndDeploy: () => Promise<void> | void;
  parameters?: LaunchableParameter[];
  parameterValues?: Record<string, string>;
  onParameterValuesChange?: (values: Record<string, string>) => void;
  parameterSecretSelections?: Record<string, LaunchableManagedSecretReference>;
  onParameterSecretSelectionsChange?: (
    selections: Record<string, LaunchableManagedSecretReference>,
  ) => void;
  availableSecrets?: ManagedSecretSummary[];
  secretsLoading?: boolean;
  organizationId?: string;
  hasMissingRequiredParameters?: boolean;
}

export const NgcApiKeyDeployWithoutKeyConfirmation: React.FC<
  NgcApiKeyDeployWithoutKeyConfirmationProps
> = ({
  open,
  onOpenChange,
  steps,
  stepIndex,
  computeHasErrors,
  computeIsOutOfStock = false,
  selectedCompute,
  onSelectCompute,
  onNextStep,
  onConfirmDeploy,
  onIncludeNgcApiKeyAndDeploy,
  parameters = [],
  parameterValues = {},
  onParameterValuesChange,
  parameterSecretSelections = {},
  onParameterSecretSelectionsChange,
  availableSecrets = [],
  secretsLoading = false,
  organizationId,
  hasMissingRequiredParameters = false,
}) => {
  const [shouldCreateNgcApiKey, setShouldCreateNgcApiKey] = useState(false);
  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setShouldCreateNgcApiKey(false);
    }
    onOpenChange(nextOpen);
  };

  const handleContinue = async () => {
    if (!isLastStep) {
      onNextStep();
      return;
    }

    const shouldIncludeKey = shouldCreateNgcApiKey;
    setShouldCreateNgcApiKey(false);

    if (currentStep === "ngc-api-key" && shouldIncludeKey) {
      await onIncludeNgcApiKeyAndDeploy();
      return;
    }

    await onConfirmDeploy();
  };

  const primaryActionLabel = (() => {
    if (currentStep === "compute") {
      return "Next";
    }
    if (!isLastStep) {
      return "Next";
    }
    if (currentStep === "ngc-api-key") {
      return shouldCreateNgcApiKey ? "Continue" : "Continue without key";
    }
    return "Deploy";
  })();

  const primaryActionDisabled =
    (currentStep === "compute" && computeHasErrors) ||
    (currentStep === "parameters" && hasMissingRequiredParameters);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[calc(100vh-2rem)] gap-6 overflow-y-auto p-8 sm:max-w-[500px]">
        <DialogHeader className="gap-3 pr-6">
          {steps.length > 1 && (
            <p className="text-xs font-medium uppercase tracking-wide text-secondary">
              Step {stepIndex + 1} of {steps.length}
            </p>
          )}
          <DialogTitle className="text-primary leading-7">
            {currentStep === "compute"
              ? "Select available compute"
              : currentStep === "parameters"
                ? "Fix variable errors"
                : "Are you sure you don't want to preconfigure your agent with an NVIDIA API key?"}
          </DialogTitle>
          <DialogDescription asChild className="text-secondary">
            <div className="space-y-3 text-sm leading-6">
              {currentStep === "compute" && (
                <p>
                  The selected compute is currently unavailable or has invalid
                  settings. Choose available compute before continuing.
                </p>
              )}
              {currentStep === "parameters" && (
                <p>
                  Update the variables with errors. You can continue once all
                  required values are valid.
                </p>
              )}
              {currentStep === "ngc-api-key" && (
                <>
                  <p>
                    To make this AI agent work, we need to connect it to an AI
                    provider. By default, we use{" "}
                    <a
                      href="https://build.nvidia.com/models"
                      target="_blank"
                      rel="noreferrer"
                      className="underline text-highlight"
                    >
                      build.nvidia.com
                    </a>
                    .
                  </p>
                  <p>
                    If you continue without setting up a key right now, the
                    agent will still be created, but you&apos;ll just need to
                    manually create and paste in an AI access key later before
                    it can start working.
                  </p>
                </>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        {currentStep === "compute" && (
          <div className="space-y-3 rounded-md border border-zinc-800 bg-secondaryBg/40 p-4">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-secondary">
                  Selected compute
                </p>
                {computeHasErrors && (
                  <span className="inline-flex items-center rounded border border-red-900/80 bg-red-950/50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-red-300">
                    {computeIsOutOfStock ? "Out of stock" : "Invalid"}
                  </span>
                )}
              </div>
              {selectedCompute ? (
                <p className="mt-1 text-sm font-normal leading-5 text-zinc-300">
                  {selectedCompute.details.map((detail, index) => (
                    <React.Fragment key={detail}>
                      {index > 0 && " · "}
                      <span
                        className={
                          index === 0 && selectedCompute.highlightFirstDetail
                            ? "text-highlight"
                            : undefined
                        }
                      >
                        {detail}
                      </span>
                    </React.Fragment>
                  ))}
                  {selectedCompute.providerName && (
                    <> · {selectedCompute.providerName}</>
                  )}
                </p>
              ) : (
                <p className="mt-1 text-sm text-secondary">
                  No compute selected
                </p>
              )}
            </div>
            <DialogButton
              variant={computeHasErrors ? "primary" : "secondary"}
              className="h-10 w-full"
              onClick={onSelectCompute}
            >
              Choose different compute
            </DialogButton>
          </div>
        )}
        {currentStep === "parameters" && onParameterValuesChange && (
          <div className="overflow-hidden rounded-md border border-zinc-800 bg-secondaryBg/40">
            <LaunchableParameterBindingForm
              parameters={parameters}
              values={parameterValues}
              onChange={onParameterValuesChange}
              secretSelections={parameterSecretSelections}
              onSecretSelectionsChange={onParameterSecretSelectionsChange}
              availableSecrets={availableSecrets}
              secretsLoading={secretsLoading}
              organizationId={organizationId}
              hideHeader
              showValidationErrors
            />
          </div>
        )}
        {currentStep === "ngc-api-key" && (
          <div className="flex items-start gap-3 rounded-md border border-zinc-800 bg-secondaryBg/40 p-4">
            <Checkbox
              id="confirm-create-ngc-api-key"
              className="mt-0.5"
              checked={shouldCreateNgcApiKey}
              onCheckedChange={(nextChecked) =>
                setShouldCreateNgcApiKey(nextChecked === true)
              }
            />
            <label
              htmlFor="confirm-create-ngc-api-key"
              className="text-sm font-medium leading-snug text-primary cursor-pointer"
            >
              Configure this agent with an NVIDIA API key
            </label>
          </div>
        )}
        <DialogFooter className="gap-3 pt-1">
          <DialogButton
            variant="secondary"
            className="h-10"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </DialogButton>
          <DialogButton
            className="h-10"
            disabled={primaryActionDisabled}
            onClick={handleContinue}
          >
            {primaryActionLabel}
          </DialogButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
